import { useCallback, useEffect, useRef, useState } from "react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { LaborPayInvoiceLine } from "@/lib/manufacturing/laborPayInvoiceLine";
import {
  getManufacturingLaborPayInvoice,
  recalculateManufacturingLaborPayInvoice,
  type LaborPayInvoiceDetail,
} from "@/lib/manufacturing/laborPayListClient";
import { laborPaysByShort } from "@/lib/manufacturing/laborPayQuantity";

function money(n: number): string {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function linePhrase(line: LaborPayInvoiceLine): string {
  if (line.labor === "included" && line.included_reason === "package") return "Included in package";
  if (line.labor === "included") return "Included in building";
  if (line.labor === "needs_pays_by") return "Needs Pays by";
  if (line.labor === "needs_size") return "Needs building size";
  if (line.labor === "unmatched") return "No labor rate";
  const pays = line.pays_by ? laborPaysByShort(line.pays_by) : "rate";
  const rate =
    line.rate_usd == null
      ? line.rate_label ?? "rate"
      : `${line.rate_label ?? "Rate"} ${money(line.rate_usd)}`;
  return `Charged · ${pays} × ${line.count} · ${rate}`;
}

export function ManufacturingLaborPayInvoiceLines({
  unitId,
  readOnly = false,
  onUpdated,
}: {
  unitId: string;
  readOnly?: boolean;
  onUpdated?: () => void;
}) {
  const [invoice, setInvoice] = useState<LaborPayInvoiceDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;

  const load = useCallback(
    async (recalculate: boolean) => {
      setBusy(true);
      setError(null);
      const res = recalculate
        ? await recalculateManufacturingLaborPayInvoice(unitId)
        : await getManufacturingLaborPayInvoice(unitId);
      setBusy(false);
      if (!res.ok) {
        setError(res.error);
        return;
      }
      setInvoice(res.data.invoice);
      onUpdatedRef.current?.();
    },
    [unitId]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const lineTotal = invoice?.lines.reduce((sum, line) => sum + line.amount_usd, 0) ?? 0;
  const payoutDiffers =
    invoice != null && Math.round(lineTotal * 100) !== Math.round(invoice.payout_usd * 100);

  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-900/60">
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
      {busy && !invoice ? <p className="text-sm text-gray-500 dark:text-zinc-400">Loading lines…</p> : null}
      {invoice ? (
        <>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-gray-600 dark:text-zinc-400">
              {invoice.lines.length} line{invoice.lines.length === 1 ? "" : "s"}
              {invoice.lines_computed_at
                ? ` · itemized ${new Date(invoice.lines_computed_at).toLocaleString()}`
                : ""}
              {payoutDiffers ? ` · line total ${money(lineTotal)} · saved invoice ${money(invoice.payout_usd)}` : ""}
            </p>
            {!readOnly && !invoice.frozen ? (
              <button
                type="button"
                className={ghostBtnClass}
                disabled={busy}
                onClick={() => void load(true)}
              >
                Recalculate
              </button>
            ) : null}
          </div>
          {invoice.lines.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-zinc-400">No option lines on this unit.</p>
          ) : (
            <ul className="space-y-1.5">
              {invoice.lines.map((line) => (
                <li key={`${line.key}-${line.labor_rate_id ?? "none"}`} className="text-sm">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-gray-900 dark:text-zinc-100">
                      {line.label} × {line.quantity}
                      {line.price != null ? (
                        <span className="ml-2 text-xs text-gray-500 dark:text-zinc-400">
                          IdeaRoom {money(line.price)}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs font-medium text-gray-800 dark:text-zinc-200">
                      {line.labor === "matched" ? money(line.amount_usd) : ""}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-zinc-400">
                    {linePhrase(line)}
                    {line.model === "unmatched" ? " · not in models" : ""}
                    {line.model === "matched" && line.model_label ? ` · ${line.model_label}` : ""}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </div>
  );
}
